import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SpinnerGap, Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { googleLogin, useAuth } from "@/lib/auth";
import { LogoMark } from "@/components/common";

const inp = "mt-1.5 h-12 w-full rounded-xl border-2 border-[#E3E8F0] px-4 text-base text-[#0E1729] outline-none transition-colors duration-200 focus:border-[#0E1729]";

export function AuthShell({ title, sub, children }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[#F5F7FA] px-5 py-10" data-testid="auth-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0E1729]" data-testid="auth-back-home"><ArrowLeft /> Back to home</Link>
        <div className="rise rounded-3xl bg-white p-8 shadow-sm">
          <LogoMark size={36} />
          <h1 className="mt-6 font-display text-3xl font-medium tracking-tight text-[#0E1729]">{title}</h1>
          {sub && <p className="mt-2 text-sm text-slate-500">{sub}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

export function PwInput({ value, onChange, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} required minLength={autoComplete === "new-password" ? 8 : 1} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} className={`${inp} pr-12`} data-testid={testId} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-[1.15rem] text-slate-400 hover:text-[#0E1729]" data-testid={`${testId}-toggle`}>{show ? <EyeSlash size={20} /> : <Eye size={20} />}</button>
    </div>
  );
}

function GoogleButton() {
  return (
    <button type="button" onClick={googleLogin} data-testid="google-login-button" className="flex h-12 w-full items-center justify-center gap-3 rounded-full border-2 border-[#E3E8F0] font-semibold text-[#0E1729] transition-colors duration-200 hover:bg-[#F5F7FA]">
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
      Continue with Google
    </button>
  );
}

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const signup = mode === "signup";
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  useEffect(() => { if (user && !user.otp_required) nav(["owner", "admin"].includes(user.role) ? "/admin" : "/dashboard", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post(signup ? "/auth/register" : "/auth/login", signup ? f : { email: f.email, password: f.password });
      setUser(r.data);
      nav("/dashboard", { replace: true });
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <AuthShell title={signup ? "Start your free trial" : "Welcome back"} sub={signup ? "Create your Hostkey account." : "Sign in to your Hostkey account."}>
      <div className="mt-6"><GoogleButton /></div>
      <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400"><span className="h-px flex-1 bg-[#E3E8F0]" /> or with email <span className="h-px flex-1 bg-[#E3E8F0]" /></div>
      <form onSubmit={submit} className="space-y-4" data-testid={signup ? "signup-form" : "signin-form"}>
        {signup && <label className="block text-sm font-medium text-[#0E1729]">Your name<input required minLength={2} value={f.name} onChange={set("name")} autoComplete="name" className={inp} data-testid="auth-name-input" /></label>}
        <label className="block text-sm font-medium text-[#0E1729]">Email<input type="email" required value={f.email} onChange={set("email")} autoComplete="email" className={inp} data-testid="auth-email-input" /></label>
        <div className="text-sm font-medium text-[#0E1729]">
          <div className="flex items-center justify-between">Password{!signup && <Link to="/forgot-password" className="text-xs font-semibold text-slate-500 hover:text-[#0E1729]" data-testid="forgot-password-link">Forgot password?</Link>}</div>
          <PwInput value={f.password} onChange={set("password")} autoComplete={signup ? "new-password" : "current-password"} testId="auth-password-input" />
          {signup && <p className="mt-1 text-xs font-normal text-slate-400">At least 8 characters.</p>}
        </div>
        {err && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="auth-error">{err}</p>}
        <button disabled={busy} data-testid="auth-submit-button" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#0E1729] font-semibold text-white transition-colors duration-200 hover:bg-[#1B2A47] disabled:opacity-60">
          {busy ? <SpinnerGap className="animate-spin" /> : signup ? "Create account" : "Sign in"}
        </button>
        <p className="text-center text-xs text-slate-400">We'll email you a 6-digit code to finish signing in.</p>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        {signup ? "Already have an account? " : "New to Hostkey? "}
        <button onClick={() => { setMode(signup ? "signin" : "signup"); setErr(""); }} className="font-semibold text-[#0E1729] underline-offset-4 hover:underline" data-testid="auth-switch-mode">{signup ? "Sign in" : "Create an account"}</button>
      </p>
    </AuthShell>
  );
}
